import { tr } from '@/lib/i18n-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Alert, Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { bannerFor, payerCounts } from '../insurance/authorisation';
import { useAuthorisation } from './AuthorisationContext';

export function AuthorisationBanner({ encounterId }: { encounterId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { summary, invalidate } = useAuthorisation();
  const model = bannerFor(summary);

  const send = useMutation({
    mutationFn: () => post(`/encounters/${encounterId}/authorisation/send`, {}),
    onSuccess: () => {
      toast.success(tr('opd', 'sent_for_authorisation'));
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['encounter', encounterId] });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_send_for_authorisation'), describeError(error)),
  });

  if (!model || !summary) return null;

  return (
    <Alert variant={model.variant} className="mb-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 font-semibold">
          <ShieldCheck className="h-4 w-4" /> {tr('opd', model.titleKey, { count: model.count })}
        </span>
        {model.showSend && (
          <Button size="sm" onClick={() => send.mutate()} loading={send.isPending}>
            {tr('opd', 'send_for_authorisation')}
          </Button>
        )}
      </div>
      {summary.payers.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs">
          {summary.payers.map((payer) => (
            <li key={payer.id} className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{payer.name}</span>
              <Badge variant="muted">{tr('opd', payer.channel === 'nphies' ? 'channel_nphies' : 'channel_manual')}</Badge>
              <span className="text-muted-foreground">{tr('opd', 'x_to_send_x_waiting_x_answered', { ...payerCounts(summary, payer.id) })}</span>
            </li>
          ))}
        </ul>
      )}
      {model.note === 'manual' && <p className="mt-1 text-xs">{tr('opd', 'manual_payer_note')}</p>}
      {model.note === 'automatic' && <p className="mt-1 text-xs">{tr('opd', 'closing_sends_for_authorisation')}</p>}
    </Alert>
  );
}
